import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { ModeDefinition } from "@picnic-field/local-host/protocol";
import {
  CHARACTER_KINDS,
  FAR_KINDS,
  FIGURE_IDLES,
  GROUND_PATTERNS,
  inKeepClear,
  ITEM_KINDS,
  normalizeStageDecor,
  PROP_KINDS,
  STAGE_DECOR_AREA,
  STAGE_DECOR_FRONT_HALF,
  STAGE_DECOR_FRONT_Z,
  STAGE_DECOR_LIMITS,
  STAGE_DECOR_PEDESTAL,
  stageDecorOf,
} from "@picnic-field/renderer/stageDecor";
import type { StageDecorData, StageDecorIssue } from "@picnic-field/renderer/stageDecor";
import { stageTheme } from "@picnic-field/renderer/stageScenes";
import type {
  FarLayer,
  FigureSpec,
  PropSpec,
  StageModelSpec,
} from "@picnic-field/renderer/stageScenes";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner } from "../../../components/Shell";
import { ZoomControls } from "../../../components/ZoomControls";
import { useOrbitDrag } from "../../../components/useOrbitDrag";
import { useT } from "../../../i18n";
import { ItemPreview, courseDeps } from "../../course";
import type { CourseViewLike } from "../../course";
import type { DecorPreviewItem } from "@picnic-field/renderer";
import {
  lessonKey,
  readStageModels,
  STAGE_MODE_ID,
  stageModelUrlFor,
  stagePresets,
} from "../../stage";
import type { StageModelEntry } from "../../stage";
import {
  buildMbStage,
  cleanDefaultDecor,
  defaultTemplateOf,
  footprintOf,
  inFootprint,
  MB_FOLLOW,
  mbBodiesOf,
  mbDecorKey,
  SCENES as MB_SCENES,
  themeOfScene,
} from "../../microbit";
import type { Footprint, SceneId } from "../../microbit";
import { Conflict, ValidationError } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { CatalogItem, Json } from "../types";
import { fmtDate } from "./ConfigTab";
import { StagePicker } from "./StagePicker";
import type { StageProduct } from "./StagePicker";

/**
 * FS-15-07 기구 무대 꾸밈(2026-10-08 · 운영자 꾸밈 편집기) — 무대마다 소품 · 손님 · 원경 · 바닥을 인벤토리로 놓아 꾸민다.
 *  - 왼쪽: 기구 무대 모드(배포본)의 무대 목록 · 꾸밈 상태(기본 장면 · 꾸밈 vN · 초안)
 *  - 가운데: 위에서 본 배치도(SVG — 끌어서 옮기기 · 빗금은 비워 둘 곳) · 3D 미리보기(실제 렌더러 · 기계 자리는 상자)
 *  - 아래: 인벤토리(소품 · 캐릭터 · 물건 · 올린 모델) · 고른 것 속성 · 바닥 · 원경 · 버전(불러오기 · 이 버전 배포 = 되돌리기)
 *  - 3D 모델 올리기(2026-10-08): GLB → POST /api/ops/stage-models(sha256) → 카탈로그 stage_model(키 · 이름 · 기본 키) 배포 → 인벤토리 '올린 모델'
 *    (파일은 Bearer 로 받아 blob URL 로 렌더러에 — 학생 무대도 같다)
 * 저장 = 카탈로그 stage_scene(key = 무대 프리셋 키 · payload = StageDecorData) 초안 → 배포(키당 활성 1) · 끄기 = 기본 장면.
 * 꾸밈은 화면 전용 — 판정 · 물리 · 리플레이 해시와 무관하다(렌더러 normalizeStageDecor 가 학생 화면에서도 같은 규칙으로 거른다).
 */
const TYPE = "stage_scene";
const ZOOMS = [3, 6, 10] as const;

type Decor = {
  props: PropSpec[];
  figures: FigureSpec[];
  far: FarLayer[];
  models: StageModelSpec[];
  ground: NonNullable<StageDecorData["ground"]>;
  deck: string;
};
/** 꾸밀 무대 하나 — 기구 무대 프리셋 또는 마이크로비트 장면 */
interface StageEntry {
  readonly group: "sb" | "mb";
  /** 카탈로그 stage_scene 키 */
  readonly key: string;
  readonly lesson: string;
  readonly title: string;
  /** 기본 무대 장면 */
  readonly scene: string;
  /** 마이크로비트 장면이면 그 장면(미리보기 템플릿 · 겹침 검사) */
  readonly mbScene?: SceneId;
}
type Sel = { list: "props" | "figures" | "models"; i: number } | null;
type Cat = "props" | "chars" | "items" | "models";

const startOf = (scene: string, payload: Json | null): Decor => {
  const base = stageTheme(scene);
  const d = payload ? normalizeStageDecor(payload, base).theme : base;
  const s = stageDecorOf(d);
  return {
    props: [...(s.props ?? [])],
    figures: [...(s.figures ?? [])],
    far: [...(s.far ?? [])],
    models: [...(s.models ?? [])],
    ground: s.ground ?? base.ground,
    deck: s.deck ?? base.deck,
  };
};
const toPayload = (d: Decor): StageDecorData => ({ version: 1, ...d });
const r2 = (v: number) => Math.round(v * 100) / 100;
const numIn = (s: string, fallback: number) => {
  const v = Number(s);
  return Number.isFinite(v) ? v : fallback;
};

// 미리보기 몸체 — 무대 판 · 받침대 · 기계 자리 상자(dev/stagePreview 와 같다)
const PREVIEW_BODIES = [
  {
    index: 0,
    kind: "chassis",
    owner: "me",
    shape: { type: "box" as const, half: [0.08, 0.1, 0.08] as const },
  },
  {
    index: 1,
    kind: "env",
    owner: "env",
    surface: "ground",
    shape: { type: "box" as const, half: [0.8, 0.1, 0.8] as const },
  },
  {
    index: 2,
    kind: "env",
    owner: "env",
    surface: "pedestal",
    shape: { type: "box" as const, half: [0.25, 0.03, 0.25] as const },
  },
];
const PREVIEW_TF = new Float32Array([
  0, 0.16, 0, 0, 0, 0, 1, 0, -0.1, 0, 0, 0, 0, 1, 0, 0.03, 0, 0, 0, 0, 1,
]);
const PREVIEW_FOLLOW = { back: -1.25, up: 0.5, side: 0.15, lookAhead: 0, stiffness: 3 };

export function StageDecorTab({ api }: { api: OpsApi }) {
  const t = useT();
  const id = useId();
  const [presets, setPresets] = useState<ReturnType<typeof stagePresets> | null>(null);
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [decor, setDecor] = useState<Decor | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sel, setSel] = useState<Sel>(null);
  const [cat, setCat] = useState<Cat>("props");
  const [kind, setKind] = useState<string>(PROP_KINDS[0]);
  const [zoom, setZoom] = useState<(typeof ZOOMS)[number]>(3);
  const [name, setName] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<StageDecorIssue[]>([]);
  const [busy, setBusy] = useState(false);
  /** 고른 제품(스마트브릭 기구 무대 · 마이크로비트 놀이터) */
  const [product, setProduct] = useState<StageProduct>("sb");
  /** 인벤토리 미리보기 창(2026-10-08) — 고른 종류 · 올리려고 고른 파일 하나 */
  const [preview, setPreview] = useState<DecorPreviewItem | null>(null);
  const [modelRows, setModelRows] = useState<CatalogItem[]>([]);
  const models = useMemo(() => readStageModels(modelRows), [modelRows]);
  const modelUrl = useMemo(
    () => stageModelUrlFor(models, (sha) => api.stageModelBytes(sha)),
    [api, models],
  );

  // 인벤토리에서 고른 종류 → 미리보기(올린 모델은 받아서)
  useEffect(() => {
    let alive = true;
    if (cat === "models") {
      if (!models.has(kind)) setPreview(null);
      else void modelUrl(kind).then((u) => alive && setPreview(u ? { glbUrl: u } : null));
    } else if (cat === "props")
      setPreview({
        prop:
          kind === "bunting"
            ? { kind: "bunting", at: [0, 0], to: [1.2, 0] }
            : { kind: kind as PropSpec["kind"], at: [0, 0] },
      });
    else
      setPreview({
        figure: {
          ...(cat === "chars"
            ? { char: kind as FigureSpec["char"] }
            : { item: kind as FigureSpec["item"] }),
          at: [0, 0, 0],
          size: 0.3,
        },
      });
    return () => {
      alive = false;
    };
  }, [cat, kind, models, modelUrl]);

  const loadModels = useCallback(async () => {
    try {
      setModelRows(await api.catalog("stage_model"));
    } catch {
      setModelRows([]);
    }
  }, [api]);

  const load = useCallback(async () => {
    try {
      setItems(await api.catalog(TYPE));
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    }
  }, [api]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const modes = await api.modes();
        const vs = modes.find((m) => m.modeId === STAGE_MODE_ID)?.versions ?? [];
        const v = vs.find((x) => x.enabled) ?? vs[vs.length - 1];
        const def = v ? await api.modeDefinition(STAGE_MODE_ID, v.version) : null;
        if (alive) setPresets(stagePresets(def as unknown as ModeDefinition | null));
      } catch (e) {
        if (alive) setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      }
    })();
    void load();
    void loadModels();
    return () => {
      alive = false;
    };
  }, [api, load, loadModels]);

  // 무대 목록 — 스마트브릭 기구 무대(모드 프리셋) + 마이크로비트 놀이터 장면(2026-10-08 · 키 'mb-<장면>')
  const entries = useMemo<StageEntry[] | null>(
    () =>
      presets === null
        ? null
        : [
            ...presets.map((p) => ({
              group: "sb" as const,
              key: p.key,
              lesson: p.stage!.lesson,
              title: t(`stage.lessons.${lessonKey(p.key)}.title`),
              scene: p.stage!.scene,
            })),
            ...MB_SCENES.map((sc) => ({
              group: "mb" as const,
              key: mbDecorKey(sc.id),
              mbScene: sc.id,
              lesson: sc.lesson ?? "MB",
              title: t(`student.microbit.scene.${sc.id}.name`),
              scene: themeOfScene(sc.id),
            })),
          ],
    [presets, t],
  );
  const preset = entries?.find((p) => p.key === key) ?? null;
  const scene = preset?.scene ?? null;
  const rowsOf = (k: string) =>
    (items ?? []).filter((i) => i.key === k).sort((a, b) => b.version - a.version);
  const rows = key ? rowsOf(key) : [];
  const live = rows.find((r) => r.enabled) ?? null;

  function open(k: string, from?: CatalogItem | null) {
    const p = entries?.find((x) => x.key === k);
    if (!p) return;
    const src = from === undefined ? (rowsOf(k).find((r) => r.enabled) ?? null) : from;
    setKey(k);
    // 마이크로비트 템플릿 장면은 길 · 칸 자리를 비운 기본 꾸밈에서 시작한다(학생 화면의 기본과 같다)
    const base = p.mbScene ? (cleanDefaultDecor(p.mbScene) as unknown as Json | null) : null;
    setDecor(startOf(p.scene, src?.payload ?? base));
    setName(src?.i18n.ko || `${p.lesson} ${p.title}`);
    setSel(null);
    setDirty(false);
    setNotice(null);
    setFailure(null);
    setServerIssues([]);
  }

  const edit = (fn: (d: Decor) => Decor) => {
    setDecor((d) => (d ? fn(d) : d));
    setDirty(true);
  };
  const patchSel = (patch: Partial<PropSpec> | Partial<FigureSpec> | Partial<StageModelSpec>) => {
    if (!sel) return;
    edit((d) => ({
      ...d,
      [sel.list]: (d[sel.list] as object[]).map((x, i) => (i === sel.i ? { ...x, ...patch } : x)),
    }));
  };

  const issues = useMemo(
    () => (decor && scene ? normalizeStageDecor(toPayload(decor), stageTheme(scene)).issues : []),
    [decor, scene],
  );
  // 마이크로비트 장면 — 학생 화면의 템플릿(달리기 길 · 격자 · 광장)이 차지하는 자리 · 그 자리에 놓인 꾸밈(그리기는 막지 않는다)
  const mbScene = preset?.mbScene ?? null;
  const footprint = useMemo(
    () => (mbScene ? footprintOf(defaultTemplateOf(mbScene)) : null),
    [mbScene],
  );
  const overlaps = useMemo(() => {
    if (!decor || !footprint) return [];
    const out: { path: string; message: string }[] = [];
    const msg = t("ops.stages.overlap");
    decor.props.forEach(
      (p, i) =>
        inFootprint(footprint, p.at[0], p.at[1], 0.12 * (p.scale ?? 1)) &&
        out.push({ path: `props[${i}]`, message: msg }),
    );
    decor.figures.forEach(
      (f, i) =>
        inFootprint(footprint, f.at[0], f.at[2], f.size * 0.4) &&
        f.at[1] < 0.4 &&
        out.push({ path: `figures[${i}]`, message: msg }),
    );
    decor.models.forEach(
      (m, i) =>
        inFootprint(footprint, m.at[0], m.at[2], m.size * 0.4) &&
        out.push({ path: `models[${i}]`, message: msg }),
    );
    return out;
  }, [decor, footprint, t]);

  function place() {
    if (!decor) return;
    if (cat === "models") {
      const m = models.get(kind);
      if (!m) return;
      if (decor.models.length >= STAGE_DECOR_LIMITS.models) return setNotice(t("ops.stages.full"));
      const spec: StageModelSpec = { model: m.key, at: [1.5, 0, -1.5], size: m.height };
      edit((d) => ({ ...d, models: [...d.models, spec] }));
      setSel({ list: "models", i: decor.models.length });
    } else if (cat === "props") {
      if (decor.props.length >= STAGE_DECOR_LIMITS.props) return setNotice(t("ops.stages.full"));
      const p: PropSpec =
        kind === "bunting"
          ? { kind: "bunting", at: [-1.5, -2], to: [1.5, -2] }
          : { kind: kind as PropSpec["kind"], at: [1.5, -1.5] };
      edit((d) => ({ ...d, props: [...d.props, p] }));
      setSel({ list: "props", i: decor.props.length });
    } else {
      if (decor.figures.length >= STAGE_DECOR_LIMITS.figures)
        return setNotice(t("ops.stages.full"));
      const f: FigureSpec = {
        ...(cat === "chars"
          ? { char: kind as FigureSpec["char"] }
          : { item: kind as FigureSpec["item"] }),
        at: [-1.2, 0, -1.2],
        size: 0.3,
        idle: "bob",
      };
      edit((d) => ({ ...d, figures: [...d.figures, f] }));
      setSel({ list: "figures", i: decor.figures.length });
    }
  }

  function remove() {
    if (!sel) return;
    edit((d) => ({ ...d, [sel.list]: (d[sel.list] as object[]).filter((_, i) => i !== sel.i) }));
    setSel(null);
  }

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setFailure(null);
    setServerIssues([]);
    try {
      const msg = await fn();
      await load();
      setNotice(msg);
    } catch (e) {
      if (e instanceof ValidationError) setServerIssues(e.issues);
      else if (e instanceof Conflict) setFailure("HTTP-409");
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  // 초안은 마지막 행이 배포 안 된 초안이면 그 버전을 덮고, 아니면 다음 버전
  const draftVersion = () =>
    rows[0] && !rows[0].publishedAt ? rows[0].version : (rows[0]?.version ?? 0) + 1;
  const save = (publishAfter: boolean) =>
    run(async () => {
      if (!key || !decor) return "";
      const v = draftVersion();
      const ko = name.trim() || key;
      await api.saveCatalog(TYPE, key, v, {
        i18n: { ko, en: `${key} stage decor` },
        payload: toPayload(decor) as unknown as Json,
      });
      if (publishAfter) await api.publishCatalog(TYPE, key, v);
      setDirty(false);
      return t(publishAfter ? "ops.stages.published" : "ops.stages.saved", { key, v });
    });

  return (
    <div className="pf-ops-stages" data-testid="ops-stages">
      {failure && (
        <ErrorBanner
          cause={t("ops.common.saveFailed")}
          action={t("ops.common.retry")}
          code={failure}
        />
      )}
      <StagePicker
        product={product}
        onProduct={(p) => {
          setProduct(p);
          setKey(null);
          setDecor(null);
          setSel(null);
          setNotice(null);
        }}
        options={
          entries
            ?.filter((p) => p.group === product)
            .map((p) => {
              const rs = rowsOf(p.key);
              const on = rs.find((r) => r.enabled);
              const draft = rs[0] && !rs[0].publishedAt ? rs[0] : null;
              return {
                key: p.key,
                label: `${p.lesson} · ${p.title}`,
                state:
                  (on
                    ? t("ops.stages.stateCustom", { v: on.version })
                    : t("ops.stages.stateDefault")) +
                  (draft ? ` · ${t("ops.stages.stateDraft", { v: draft.version })}` : ""),
              };
            }) ?? null
        }
        value={key}
        onPick={(k) => open(k)}
        testId="stage-pick"
      />
      <div>
        <section className="pf-pane" aria-labelledby={`${id}-ed`}>
          {!preset || !decor || !scene ? (
            <>
              <h2 id={`${id}-ed`} className="pf-h3">
                {t("ops.stages.title")}
              </h2>
              <p className="pf-muted">{t("ops.stages.pick")}</p>
            </>
          ) : (
            <>
              <h2 id={`${id}-ed`} className="pf-h3" data-testid="stage-head">
                {t("ops.stages.head", { lesson: preset.lesson, title: preset.title })}
              </h2>
              <p className="pf-muted">
                {t("ops.stages.scene", { scene })} ·{" "}
                {t("ops.stages.count", {
                  p: decor.props.length,
                  pm: STAGE_DECOR_LIMITS.props,
                  f: decor.figures.length,
                  fm: STAGE_DECOR_LIMITS.figures,
                  r: decor.far.length,
                  rm: STAGE_DECOR_LIMITS.far,
                })}{" "}
                ·{" "}
                {t("ops.stages.countModels", {
                  m: decor.models.length,
                  mm: STAGE_DECOR_LIMITS.models,
                })}
                {dirty && <strong> · {t("ops.stages.dirty")}</strong>}
              </p>
              <div className="pf-ops-stages__views">
                <DecorMap
                  decor={decor}
                  sel={sel}
                  span={zoom}
                  label={t("ops.stages.map")}
                  nameOf={(k) => t(`ops.stages.kind.${k}`)}
                  modelName={(k) => models.get(k)?.name ?? k}
                  footprint={footprint}
                  onSelect={setSel}
                  onMove={(s, x, z) =>
                    edit((d) =>
                      s.list === "models"
                        ? {
                            ...d,
                            models: d.models.map((m, i) =>
                              i === s.i ? { ...m, at: [x, m.at[1], z] } : m,
                            ),
                          }
                        : s.list === "props"
                          ? {
                              ...d,
                              props: d.props.map((p, i) => (i === s.i ? moveProp(p, x, z) : p)),
                            }
                          : {
                              ...d,
                              figures: d.figures.map((f, i) =>
                                i === s.i ? { ...f, at: [x, f.at[1], z] } : f,
                              ),
                            },
                    )
                  }
                />
                <DecorPreview scene={scene} decor={decor} modelUrl={modelUrl} mbScene={mbScene} />
              </div>
              <div className="pf-row">
                <label>
                  {t("ops.stages.zoom")}{" "}
                  <select
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value) as (typeof ZOOMS)[number])}
                  >
                    {ZOOMS.map((z) => (
                      <option key={z} value={z}>
                        ±{z}m
                      </option>
                    ))}
                  </select>
                </label>
                <span className="pf-muted">{t("ops.stages.keepClear")}</span>
              </div>
              <p
                className={issues.length ? "pf-ops-stages__warn" : "pf-muted"}
                data-testid="stage-issues"
                role="status"
              >
                {issues.length ? t("ops.stages.issues", { n: issues.length }) : t("ops.stages.ok")}
              </p>
              {issues.length > 0 && (
                <ul className="pf-ops-stages__issues">
                  {issues.slice(0, 8).map((i) => (
                    <li key={i.path + i.message}>
                      <code>{i.path}</code> {i.message}
                    </li>
                  ))}
                </ul>
              )}
              {footprint && (
                <p
                  className={overlaps.length ? "pf-ops-stages__warn" : "pf-muted"}
                  data-testid="stage-overlaps"
                  role="status"
                >
                  {overlaps.length
                    ? t("ops.stages.overlaps", { n: overlaps.length })
                    : t("ops.stages.noOverlap")}
                </p>
              )}
              {overlaps.length > 0 && (
                <ul className="pf-ops-stages__issues">
                  {overlaps.slice(0, 8).map((i) => (
                    <li key={i.path}>
                      <code>{i.path}</code> {i.message}
                    </li>
                  ))}
                </ul>
              )}

              <div className="pf-ops-stages__tools">
                <fieldset className="pf-ops-stages__box">
                  <legend>{t("ops.stages.inventory")}</legend>
                  <div className="pf-seg pf-seg--pill" role="tablist">
                    {(["props", "chars", "items", "models"] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        role="tab"
                        aria-selected={c === cat}
                        className={`pf-ops-type${c === cat ? " pf-ops-type--on" : ""}`}
                        onClick={() => {
                          setCat(c);
                          setKind(
                            c === "models"
                              ? ([...models.keys()][0] ?? "")
                              : (c === "props"
                                  ? PROP_KINDS
                                  : c === "chars"
                                    ? CHARACTER_KINDS
                                    : ITEM_KINDS)[0],
                          );
                        }}
                      >
                        {t(`ops.stages.cat${c[0]!.toUpperCase()}${c.slice(1)}`)}
                      </button>
                    ))}
                  </div>
                  <div className="pf-ops-stages__kinds" data-testid="stage-inventory">
                    {cat === "models" && models.size === 0 && (
                      <span className="pf-muted">{t("ops.stages.noModels")}</span>
                    )}
                    {(cat === "models"
                      ? [...models.keys()]
                      : cat === "props"
                        ? PROP_KINDS
                        : cat === "chars"
                          ? CHARACTER_KINDS
                          : ITEM_KINDS
                    ).map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={`pf-ops-stages__kind${k === kind ? " is-on" : ""}`}
                        aria-pressed={k === kind}
                        data-kind={k}
                        onClick={() => setKind(k)}
                      >
                        {cat === "models" ? (models.get(k)?.name ?? k) : t(`ops.stages.kind.${k}`)}
                      </button>
                    ))}
                  </div>
                  <ItemPreview
                    item={preview}
                    label={t("ops.stages.itemPreview")}
                    failText={t("ops.stages.previewFail")}
                    testId="stage-item-preview"
                    footer={(size) =>
                      // 크기는 올린 모델(파일 단위)만 — 코드 모델은 놓을 때 정한 키로 그린다
                      size &&
                      preview &&
                      "glbUrl" in preview && (
                        <p className="pf-muted" data-testid="stage-item-size">
                          {t("ops.stages.itemSize", {
                            x: fmtM(size.x),
                            y: fmtM(size.y),
                            z: fmtM(size.z),
                          })}
                        </p>
                      )
                    }
                  />
                  <Button onClick={place} data-testid="stage-place">
                    {t("ops.stages.place")}
                  </Button>
                </fieldset>

                <fieldset className="pf-ops-stages__box" data-testid="stage-selected">
                  <legend>{t("ops.stages.selected")}</legend>
                  {sel ? (
                    <SelectedForm
                      decor={decor}
                      sel={sel}
                      onPatch={patchSel}
                      onRemove={remove}
                      modelName={(k) => models.get(k)?.name ?? k}
                    />
                  ) : (
                    <p className="pf-muted">{t("ops.stages.none")}</p>
                  )}
                </fieldset>

                <fieldset className="pf-ops-stages__box">
                  <legend>{t("ops.stages.ground")}</legend>
                  <label>
                    {t("ops.stages.pattern")}{" "}
                    <select
                      value={decor.ground.pattern}
                      onChange={(e) =>
                        edit((d) => ({
                          ...d,
                          ground: {
                            ...d.ground,
                            pattern: e.target.value as Decor["ground"]["pattern"],
                          },
                        }))
                      }
                    >
                      {GROUND_PATTERNS.map((g) => (
                        <option key={g} value={g}>
                          {t(`ops.stages.groundKind.${g}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {t("ops.stages.color1")}{" "}
                    <input
                      type="color"
                      value={decor.ground.color}
                      onChange={(e) =>
                        edit((d) => ({ ...d, ground: { ...d.ground, color: e.target.value } }))
                      }
                    />
                  </label>
                  <label>
                    {t("ops.stages.color2")}{" "}
                    <input
                      type="color"
                      value={decor.ground.color2}
                      onChange={(e) =>
                        edit((d) => ({ ...d, ground: { ...d.ground, color2: e.target.value } }))
                      }
                    />
                  </label>
                  <label>
                    {t("ops.stages.deck")}{" "}
                    <input
                      type="color"
                      value={decor.deck}
                      onChange={(e) => edit((d) => ({ ...d, deck: e.target.value }))}
                    />
                  </label>
                </fieldset>

                <fieldset className="pf-ops-stages__box" data-testid="stage-far">
                  <legend>{t("ops.stages.far")}</legend>
                  {decor.far.map((f, i) => (
                    <div key={i} className="pf-row pf-row--wrap pf-ops-stages__far">
                      <select
                        aria-label={t("ops.stages.far")}
                        value={f.kind}
                        onChange={(e) =>
                          edit((d) => ({
                            ...d,
                            far: d.far.map((x, j) =>
                              j === i ? { ...x, kind: e.target.value as FarLayer["kind"] } : x,
                            ),
                          }))
                        }
                      >
                        {FAR_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {t(`ops.stages.farKind.${k}`)}
                          </option>
                        ))}
                      </select>
                      <input
                        type="color"
                        aria-label={t("ops.stages.color")}
                        value={f.color}
                        onChange={(e) =>
                          edit((d) => ({
                            ...d,
                            far: d.far.map((x, j) =>
                              j === i ? { ...x, color: e.target.value } : x,
                            ),
                          }))
                        }
                      />
                      <label>
                        {t("ops.stages.dist")}{" "}
                        <input
                          type="number"
                          step={1}
                          value={f.dist}
                          onChange={(e) =>
                            edit((d) => ({
                              ...d,
                              far: d.far.map((x, j) =>
                                j === i ? { ...x, dist: numIn(e.target.value, x.dist) } : x,
                              ),
                            }))
                          }
                        />
                      </label>
                      <label>
                        {t("ops.stages.height")}{" "}
                        <input
                          type="number"
                          step={0.5}
                          value={f.height}
                          onChange={(e) =>
                            edit((d) => ({
                              ...d,
                              far: d.far.map((x, j) =>
                                j === i ? { ...x, height: numIn(e.target.value, x.height) } : x,
                              ),
                            }))
                          }
                        />
                      </label>
                      <Button
                        variant="ghost"
                        onClick={() =>
                          edit((d) => ({ ...d, far: d.far.filter((_, j) => j !== i) }))
                        }
                      >
                        {t("ops.stages.remove")}
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    disabled={decor.far.length >= STAGE_DECOR_LIMITS.far}
                    onClick={() =>
                      edit((d) => ({
                        ...d,
                        far: [
                          ...d.far,
                          {
                            kind: "hills",
                            color: "#8fbf7a",
                            dist: 24,
                            height: 4,
                            seed: d.far.length + 1,
                          },
                        ],
                      }))
                    }
                  >
                    {t("ops.stages.farAdd")}
                  </Button>
                </fieldset>
              </div>

              <StageModelPanel
                api={api}
                rows={modelRows}
                models={models}
                onChanged={loadModels}
                onPreview={setPreview}
              />

              <div className="pf-row pf-ops-stages__save">
                <label>
                  {t("ops.stages.name")}{" "}
                  <input
                    value={name}
                    maxLength={60}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="stage-name"
                  />
                </label>
                <Button disabled={busy} onClick={() => void save(false)} data-testid="stage-save">
                  {t("ops.stages.saveDraft")}
                </Button>
                <Button
                  variant="accent"
                  disabled={busy}
                  onClick={() => void save(true)}
                  data-testid="stage-publish"
                >
                  {t("ops.stages.publish")}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => open(preset.key, null)}>
                  {t("ops.stages.restart")}
                </Button>
                {live && (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    data-testid="stage-reset"
                    onClick={() =>
                      void run(async () => {
                        await api.disableCatalog(TYPE, preset.key);
                        return t("ops.stages.disabled", { key: preset.key });
                      })
                    }
                  >
                    {t("ops.stages.reset")}
                  </Button>
                )}
              </div>
              {notice && (
                <p className="pf-note" role="status" data-testid="stage-notice">
                  {notice}
                </p>
              )}
              {serverIssues.length > 0 && (
                <ul className="pf-ops-stages__issues" data-testid="stage-server-issues">
                  {serverIssues.map((i) => (
                    <li key={i.path + i.message}>
                      <code>{i.path}</code> {i.message}
                    </li>
                  ))}
                </ul>
              )}

              {rows.length > 0 && (
                <table className="pf-tbl" data-testid="stage-versions">
                  <caption className="pf-h4">{t("ops.stages.versions")}</caption>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.version} data-version={r.version}>
                        <th scope="row">v{r.version}</th>
                        <td>{r.i18n.ko}</td>
                        <td>
                          {r.enabled
                            ? t("ops.catalog.stateLive")
                            : r.publishedAt
                              ? t("ops.catalog.stateOff")
                              : t("ops.catalog.stateDraft")}
                          {r.publishedAt
                            ? ` · ${t("ops.catalog.publishedAt", { at: fmtDate(r.publishedAt) })}`
                            : ""}
                        </td>
                        <td>
                          <Button variant="ghost" onClick={() => open(preset.key, r)}>
                            {t("ops.stages.load")}
                          </Button>
                          {!r.enabled && (
                            <Button
                              variant="ghost"
                              disabled={busy}
                              data-testid={`stage-rollback-${r.version}`}
                              onClick={() =>
                                void run(async () => {
                                  await api.publishCatalog(TYPE, preset.key, r.version);
                                  return t("ops.stages.published", {
                                    key: preset.key,
                                    v: r.version,
                                  });
                                })
                              }
                            >
                              {t("ops.stages.rollback")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="pf-note">{t("ops.stages.note")}</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** 소품 옮기기 — 만국기는 끝점도 같이 옮긴다 */
function moveProp(p: PropSpec, x: number, z: number): PropSpec {
  if (!p.to) return { ...p, at: [x, z] };
  const dx = x - p.at[0],
    dz = z - p.at[1];
  return { ...p, at: [x, z], to: [r2(p.to[0] + dx), r2(p.to[1] + dz)] };
}

/** 위에서 본 배치도 — x 오른쪽 · z 아래(카메라 쪽). 소품 = 초록 원 · 손님 = 주황 원 · 고른 것은 굵게 */
function DecorMap({
  decor,
  sel,
  span,
  label,
  nameOf,
  modelName,
  footprint,
  onSelect,
  onMove,
}: {
  decor: Decor;
  sel: Sel;
  span: number;
  label: string;
  nameOf: (kind: string) => string;
  modelName: (key: string) => string;
  /** 마이크로비트 장면 템플릿이 차지하는 자리(없으면 그리지 않는다) */
  footprint: Footprint | null;
  onSelect: (s: Sel) => void;
  onMove: (s: NonNullable<Sel>, x: number, z: number) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<NonNullable<Sel> | null>(null);
  const font = span / 22;
  const toWorld = (e: ReactPointerEvent) => {
    const svg = svgRef.current;
    const m = svg?.getScreenCTM?.();
    if (!svg || !m) return null;
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    const lim = STAGE_DECOR_AREA;
    return {
      x: r2(Math.max(-lim, Math.min(lim, pt.x))),
      z: r2(Math.max(-lim, Math.min(lim, pt.y))),
    };
  };
  const down = (s: NonNullable<Sel>) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    onSelect(s);
    drag.current = s;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };
  const move = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    const w = toWorld(e);
    if (w) onMove(drag.current, w.x, w.z);
  };
  const up = () => (drag.current = null);
  const P = STAGE_DECOR_PEDESTAL;
  const dot = (
    s: NonNullable<Sel>,
    x: number,
    z: number,
    r: number,
    kindName: string,
    bad: boolean,
  ) => {
    const on = sel?.list === s.list && sel.i === s.i;
    return (
      <g
        key={`${s.list}-${s.i}`}
        onPointerDown={down(s)}
        className={`pf-ops-map__dot pf-ops-map__dot--${s.list}${on ? " is-on" : ""}${bad ? " is-bad" : ""}`}
        data-testid={`map-${s.list}-${s.i}`}
      >
        <circle cx={x} cy={z} r={r} strokeWidth={on ? font / 3 : font / 8} />
        <text x={x} y={z - r - font * 0.3} fontSize={font} textAnchor="middle">
          {kindName}
        </text>
      </g>
    );
  };
  return (
    <svg
      ref={svgRef}
      className="pf-ops-map"
      viewBox={`${-span} ${-span} ${span * 2} ${span * 2}`}
      role="img"
      aria-label={label}
      data-testid="stage-map"
      onPointerMove={move}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerDown={() => onSelect(null)}
    >
      <defs>
        <pattern
          id="pf-hatch"
          width={0.2}
          height={0.2}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width={0.08} height={0.2} className="pf-ops-map__hatch" />
        </pattern>
      </defs>
      <rect x={-span} y={-span} width={span * 2} height={span * 2} className="pf-ops-map__ground" />
      {[-2, -1, 1, 2].map((g) => (
        <g key={g} className="pf-ops-map__grid">
          <line x1={g * (span / 3)} y1={-span} x2={g * (span / 3)} y2={span} />
          <line x1={-span} y1={g * (span / 3)} x2={span} y2={g * (span / 3)} />
        </g>
      ))}
      <rect x={-P} y={-P} width={P * 2} height={P * 2} fill="url(#pf-hatch)" />
      <rect
        x={-STAGE_DECOR_FRONT_HALF}
        y={STAGE_DECOR_FRONT_Z}
        width={STAGE_DECOR_FRONT_HALF * 2}
        height={span}
        fill="url(#pf-hatch)"
      />
      {footprint?.kind === "rect" ? (
        <rect
          x={footprint.x0}
          y={footprint.z0}
          width={footprint.x1 - footprint.x0}
          height={footprint.z1 - footprint.z0}
          className="pf-ops-map__footprint"
          data-testid="stage-map-footprint"
        />
      ) : footprint?.kind === "circle" ? (
        <circle
          cx={footprint.cx}
          cy={footprint.cz}
          r={footprint.r}
          className="pf-ops-map__footprint"
          data-testid="stage-map-footprint"
        />
      ) : null}
      {!footprint || footprint.kind !== "rect" ? (
        <rect x={-0.25} y={-0.25} width={0.5} height={0.5} className="pf-ops-map__pedestal" />
      ) : null}
      {decor.props.map((p, i) => (
        <g key={`p${i}`}>
          {p.to && (
            <line
              x1={p.at[0]}
              y1={p.at[1]}
              x2={p.to[0]}
              y2={p.to[1]}
              className="pf-ops-map__line"
              strokeWidth={font / 6}
            />
          )}
          {dot(
            { list: "props", i },
            p.at[0],
            p.at[1],
            0.15 * (p.scale ?? 1) + font * 0.2,
            nameOf(p.kind),
            p.kind !== "helipad" && inKeepClear(p.at[0], p.at[1]),
          )}
        </g>
      ))}
      {decor.models.map((m, i) =>
        dot(
          { list: "models", i },
          m.at[0],
          m.at[2],
          Math.max(0.1, m.size * 0.4) + font * 0.15,
          modelName(m.model),
          inKeepClear(m.at[0], m.at[2], m.at[1]),
        ),
      )}
      {decor.figures.map((f, i) =>
        dot(
          { list: "figures", i },
          f.at[0],
          f.at[2],
          Math.max(0.08, f.size * 0.4) + font * 0.15,
          nameOf((f.char ?? f.item)!),
          inKeepClear(f.at[0], f.at[2], f.at[1]),
        ),
      )}
    </svg>
  );
}

/** 고른 것 속성 — 소품(자리 · 회전 · 크기 · 색 · 만국기 끝점) · 손님(자리 · 높이 · 키 · 색 · 몸짓 · 반응) */
function SelectedForm({
  decor,
  sel,
  onPatch,
  onRemove,
  modelName,
}: {
  decor: Decor;
  sel: NonNullable<Sel>;
  onPatch: (p: Partial<PropSpec> | Partial<FigureSpec> | Partial<StageModelSpec>) => void;
  onRemove: () => void;
  modelName: (key: string) => string;
}) {
  const t = useT();
  const field = (label: string, value: number, step: number, set: (v: number) => void) => (
    <label key={label}>
      {label}{" "}
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => set(numIn(e.target.value, value))}
      />
    </label>
  );
  if (sel.list === "models") {
    const m = decor.models[sel.i];
    if (!m) return null;
    return (
      <div className="pf-ops-stages__form" data-kind={m.model}>
        <strong>{modelName(m.model)}</strong>
        {field(t("ops.stages.x"), m.at[0], 0.1, (v) => onPatch({ at: [v, m.at[1], m.at[2]] }))}
        {field(t("ops.stages.z"), m.at[2], 0.1, (v) => onPatch({ at: [m.at[0], m.at[1], v] }))}
        {field(t("ops.stages.y"), m.at[1], 0.1, (v) => onPatch({ at: [m.at[0], v, m.at[2]] }))}
        {field(t("ops.stages.size"), m.size, 0.05, (v) => onPatch({ size: v }))}
        {field(t("ops.stages.rot"), m.rot ?? 0, 15, (v) => onPatch({ rot: v }))}
        <Button variant="ghost" onClick={onRemove} data-testid="stage-remove">
          {t("ops.stages.remove")}
        </Button>
      </div>
    );
  }
  if (sel.list === "props") {
    const p = decor.props[sel.i];
    if (!p) return null;
    return (
      <div className="pf-ops-stages__form" data-kind={p.kind}>
        <strong>{t(`ops.stages.kind.${p.kind}`)}</strong>
        {field(t("ops.stages.x"), p.at[0], 0.1, (v) => onPatch({ at: [v, p.at[1]] }))}
        {field(t("ops.stages.z"), p.at[1], 0.1, (v) => onPatch({ at: [p.at[0], v] }))}
        {field(t("ops.stages.rot"), p.rot ?? 0, 15, (v) => onPatch({ rot: v }))}
        {field(t("ops.stages.scale"), p.scale ?? 1, 0.1, (v) => onPatch({ scale: v }))}
        {p.to && field(t("ops.stages.toX"), p.to[0], 0.1, (v) => onPatch({ to: [v, p.to![1]] }))}
        {p.to && field(t("ops.stages.toZ"), p.to[1], 0.1, (v) => onPatch({ to: [p.to![0], v] }))}
        <label>
          {t("ops.stages.color")}{" "}
          <input
            type="color"
            value={p.color ?? "#ffffff"}
            onChange={(e) => onPatch({ color: e.target.value })}
          />
        </label>
        <Button variant="ghost" onClick={onRemove} data-testid="stage-remove">
          {t("ops.stages.remove")}
        </Button>
      </div>
    );
  }
  const f = decor.figures[sel.i];
  if (!f) return null;
  return (
    <div className="pf-ops-stages__form" data-kind={f.char ?? f.item}>
      <strong>{t(`ops.stages.kind.${(f.char ?? f.item)!}`)}</strong>
      {field(t("ops.stages.x"), f.at[0], 0.1, (v) => onPatch({ at: [v, f.at[1], f.at[2]] }))}
      {field(t("ops.stages.z"), f.at[2], 0.1, (v) => onPatch({ at: [f.at[0], f.at[1], v] }))}
      {field(t("ops.stages.y"), f.at[1], 0.1, (v) => onPatch({ at: [f.at[0], v, f.at[2]] }))}
      {field(t("ops.stages.size"), f.size, 0.05, (v) => onPatch({ size: v }))}
      <label>
        {t("ops.stages.color")}{" "}
        <input
          type="color"
          value={f.color ?? "#ffffff"}
          onChange={(e) => onPatch({ color: e.target.value })}
        />
      </label>
      <label>
        {t("ops.stages.idle")}{" "}
        <select
          value={f.idle ?? "bob"}
          onChange={(e) => onPatch({ idle: e.target.value as FigureSpec["idle"] })}
        >
          {FIGURE_IDLES.map((k) => (
            <option key={k} value={k}>
              {t(`ops.stages.idleKind.${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label>
        <input
          type="checkbox"
          checked={f.cheers !== false}
          onChange={(e) => onPatch({ cheers: e.target.checked })}
        />{" "}
        {t("ops.stages.cheers")}
      </label>
      <Button variant="ghost" onClick={onRemove} data-testid="stage-remove">
        {t("ops.stages.remove")}
      </Button>
    </div>
  );
}

/** 3D 미리보기 — 실제 렌더러(무대 장면 + 꾸밈 데이터). 무대가 바뀌면 다시 만들고, 꾸밈은 0.3초 모아 setStageDecor */
function DecorPreview({
  scene,
  decor,
  modelUrl,
  mbScene,
}: {
  scene: string;
  decor: Decor;
  modelUrl: (key: string) => Promise<string | null>;
  /** 마이크로비트 장면이면 학생 화면과 같은 템플릿(주인공 · 길 · 칸 · 광장)을 함께 그린다 */
  mbScene: SceneId | null;
}) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // 모델 목록이 바뀌어도(새로 올림) 뷰를 다시 만들지 않게 — 렌더러는 이 틀을 통해 늘 지금 목록으로 찾는다
  const modelUrlRef = useRef(modelUrl);
  modelUrlRef.current = modelUrl;
  const viewRef = useRef<CourseViewLike | null>(null);
  const decorRef = useRef(decor);
  decorRef.current = decor;
  const [state, setState] = useState<"loading" | "3d" | "fail">("loading");
  useOrbitDrag(canvasRef, () => viewRef.current, true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let alive = true;
    let handle: CourseViewLike | null = null;
    let stageObj: Awaited<ReturnType<typeof buildMbStage>> | null = null;
    setState("loading");
    void (async () => {
      try {
        const tmpl = mbScene ? defaultTemplateOf(mbScene) : null;
        stageObj = tmpl ? await buildMbStage(tmpl) : null;
        if (tmpl && stageObj) {
          stageObj.update(tmpl);
          stageObj.animate(0);
        }
        const body = tmpl ? mbBodiesOf(tmpl) : { bodies: PREVIEW_BODIES, tf: PREVIEW_TF };
        const h = await courseDeps().createView(canvas, {
          bodies: body.bodies,
          followIndex: 0,
          me: { glbUrl: null, ...(stageObj ? { object: stageObj.object } : {}) },
          stageScene: scene,
          stageThemeData: toPayload(decorRef.current),
          stageModelUrl: (k) => modelUrlRef.current(k),
          follow: tmpl ? MB_FOLLOW : PREVIEW_FOLLOW,
        });
        handle = h;
        if (!alive) return h.dispose();
        await h.ready;
        if (!alive) return;
        h.update({ tick: 0, transforms: body.tf });
        // 미리보기 창은 거의 정사각형이라 학생 화면(가로로 넓다)보다 옆이 좁다 — 길 · 칸 · 광장이 다 보이게 물러선다
        if (tmpl && tmpl.kind !== "pedestal") h.setZoom?.(1.7);
        viewRef.current = h;
        setState("3d");
      } catch {
        if (alive) setState("fail");
      }
    })();
    return () => {
      alive = false;
      handle?.dispose();
      stageObj?.dispose();
      viewRef.current = null;
    };
  }, [scene, mbScene]);

  useEffect(() => {
    if (state !== "3d") return;
    const timer = setTimeout(() => viewRef.current?.setStageDecor?.(toPayload(decor)), 300);
    return () => clearTimeout(timer);
  }, [decor, state, modelUrl]);

  return (
    <div className="pf-ops-preview" data-view={state} data-testid="stage-preview">
      <canvas
        ref={canvasRef}
        className="pf-ops-preview__canvas"
        aria-label={t("ops.stages.preview")}
      />
      {state === "3d" && <ZoomControls getView={() => viewRef.current} wheelTarget={canvasRef} />}
      {state === "fail" && <p className="pf-muted">{t("ops.stages.previewFail")}</p>}
    </div>
  );
}

/** 3D 모델 올리기(2026-10-08) — GLB 하나 → 내용 주소 → 카탈로그 stage_model 배포(인벤토리 '올린 모델'). 끄면 꾸밈에 놓인 그 모델은 그리지 않는다 */
const MODEL_KEY = /^[a-z0-9-]{1,40}$/;
const MODEL_MAX_BYTES = 4 * 1024 * 1024;

/** 파일 → 바이트(Blob.arrayBuffer 가 없는 환경은 FileReader) */
function fileBytes(f: Blob): Promise<Uint8Array> {
  if (typeof f.arrayBuffer === "function") return f.arrayBuffer().then((b) => new Uint8Array(b));
  return new Promise((ok, fail) => {
    const r = new FileReader();
    r.onload = () => ok(new Uint8Array(r.result as ArrayBuffer));
    r.onerror = () => fail(r.error);
    r.readAsArrayBuffer(f);
  });
}

function StageModelPanel({
  api,
  rows,
  models,
  onChanged,
  onPreview,
}: {
  api: OpsApi;
  rows: readonly CatalogItem[];
  models: ReadonlyMap<string, StageModelEntry>;
  onChanged: () => Promise<void>;
  /** 고른 파일을 인벤토리 미리보기 창에(올리기 전에 모양 · 크기 확인) */
  onPreview: (item: DecorPreviewItem | null) => void;
}) {
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const fileUrl = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (fileUrl.current) URL.revokeObjectURL(fileUrl.current);
    },
    [],
  );
  const pick = (f: File | null) => {
    setFile(f);
    if (fileUrl.current) URL.revokeObjectURL(fileUrl.current);
    fileUrl.current = null;
    if (!f || typeof URL.createObjectURL !== "function") return;
    fileUrl.current = URL.createObjectURL(f);
    onPreview({ glbUrl: fileUrl.current });
  };
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [height, setHeight] = useState(0.5);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function upload() {
    if (!file) return setMsg({ ok: false, text: t("ops.stages.modelNoFile") });
    if (file.size > MODEL_MAX_BYTES)
      return setMsg({ ok: false, text: t("ops.stages.modelTooBig") });
    if (!MODEL_KEY.test(key)) return setMsg({ ok: false, text: t("ops.stages.modelBadKey") });
    if (!name.trim()) return setMsg({ ok: false, text: t("ops.catalog.required") });
    setBusy(true);
    setMsg(null);
    try {
      const bytes = await fileBytes(file);
      const up = await api.uploadStageModel(bytes);
      const mine = rows.filter((r) => r.key === key).sort((a, b) => b.version - a.version);
      const v = mine[0] && !mine[0].publishedAt ? mine[0].version : (mine[0]?.version ?? 0) + 1;
      await api.saveCatalog("stage_model", key, v, {
        i18n: { ko: name.trim(), en: key },
        payload: { glb: up.sha256, height },
      });
      await api.publishCatalog("stage_model", key, v);
      await onChanged();
      setMsg({ ok: true, text: t("ops.stages.modelUploaded", { key, n: up.triangles }) });
      setFile(null);
    } catch (e) {
      const text =
        e instanceof ValidationError
          ? e.issues.map((i) => i.message).join(" · ")
          : t("ops.common.saveFailed");
      setMsg({ ok: false, text });
    } finally {
      setBusy(false);
    }
  }

  async function off(k: string) {
    setBusy(true);
    try {
      await api.disableCatalog("stage_model", k);
      await onChanged();
      setMsg({ ok: true, text: t("ops.stages.modelDisabled", { key: k }) });
    } catch {
      setMsg({ ok: false, text: t("ops.common.saveFailed") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className="pf-ops-stages__box pf-ops-stages__models" data-testid="stage-models">
      <legend>{t("ops.stages.modelsTitle")}</legend>
      <div className="pf-row pf-row--wrap pf-ops-stages__far">
        <label>
          {t("ops.stages.modelFile")}{" "}
          <input
            type="file"
            accept=".glb,model/gltf-binary"
            data-testid="stage-model-file"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
        </label>
        <label>
          {t("ops.stages.modelKey")}{" "}
          <input
            value={key}
            maxLength={40}
            placeholder="toy-robot"
            data-testid="stage-model-key"
            onChange={(e) => setKey(e.target.value.trim().toLowerCase())}
          />
        </label>
        <label>
          {t("ops.stages.modelName")}{" "}
          <input
            value={name}
            maxLength={40}
            data-testid="stage-model-name"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          {t("ops.stages.modelHeight")}{" "}
          <input
            type="number"
            step={0.05}
            min={0.05}
            max={3}
            value={height}
            onChange={(e) => setHeight(Math.min(3, Math.max(0.05, numIn(e.target.value, height))))}
          />
        </label>
        <Button disabled={busy} onClick={() => void upload()} data-testid="stage-model-upload">
          {t("ops.stages.modelUpload")}
        </Button>
      </div>
      {msg && (
        <p
          className={msg.ok ? "pf-note" : "pf-ops-stages__warn"}
          role="status"
          data-testid="stage-model-msg"
        >
          {msg.text}
        </p>
      )}
      {models.size > 0 && (
        <ul className="pf-ops-stages__issues" data-testid="stage-model-list">
          {[...models.values()].map((m) => (
            <li key={m.key} className="pf-row pf-row--wrap">
              <span>
                {m.name} <code>{m.key}</code> · {t("ops.stages.modelHeightIs", { h: m.height })}
              </span>
              <Button variant="ghost" disabled={busy} onClick={() => void off(m.key)}>
                {t("ops.stages.modelOff")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className="pf-muted">{t("ops.stages.modelNote")}</p>
    </fieldset>
  );
}

/** 미터 — 1 m 이상은 소수 둘째 · 작으면 셋째(끝 0 은 뺀다) */
const fmtM = (v: number) => (v >= 1 ? v.toFixed(2) : v.toFixed(3)).replace(/\.?0+$/, "");
